import React from 'react';
import {
  Chip,
  Box,
  Tooltip,
  Typography,
  Badge,
  Paper,
} from '@mui/material';
import {
  PriorityHigh,
  Error,
  Warning,
  SecurityOutlined,
  CheckCircle,
  Info,
  ReportProblem,
  Shield,
  GppBad,
  VerifiedUser,
} from '@mui/icons-material';

interface FraudRiskBadgeProps {
  riskLevel: string;
  riskMessage?: string;
  riskFactors?: string[];
  variant?: 'chip' | 'badge' | 'card';
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
  animated?: boolean;
  onClick?: () => void;
}

const FraudRiskBadge: React.FC<FraudRiskBadgeProps> = ({
  riskLevel,
  riskMessage,
  riskFactors = [],
  variant = 'chip',
  size = 'medium',
  showIcon = true,
  animated = false,
  onClick,
}) => {
  const getRiskConfig = (level: string) => {
    switch (level?.toUpperCase()) {
      case 'CRITICAL':
        return {
          label: 'CRITICAL RISK',
          color: '#d32f2f',
          backgroundColor: '#ffebee',
          borderColor: '#d32f2f',
          icon: <GppBad />,
          severity: 5,
          pulse: true,
        };
      case 'HIGH':
        return {
          label: 'HIGH RISK',
          color: '#f57c00',
          backgroundColor: '#fff3e0',
          borderColor: '#f57c00',
          icon: <ReportProblem />,
          severity: 4,
          pulse: false,
        };
      case 'MEDIUM':
        return {
          label: 'MEDIUM RISK',
          color: '#f9a825',
          backgroundColor: '#fffde7',
          borderColor: '#f9a825',
          icon: <Warning />,
          severity: 3,
          pulse: false,
        };
      case 'LOW':
        return {
          label: 'LOW RISK',
          color: '#388e3c',
          backgroundColor: '#e8f5e9',
          borderColor: '#388e3c',
          icon: <SecurityOutlined />,
          severity: 2,
          pulse: false,
        };
      case 'LEGITIMATE':
        return {
          label: 'VERIFIED',
          color: '#2e7d32',
          backgroundColor: '#e8f5e9',
          borderColor: '#2e7d32',
          icon: <VerifiedUser />,
          severity: 1,
          pulse: false,
        };
      default:
        return {
          label: 'UNKNOWN',
          color: '#757575',
          backgroundColor: '#f5f5f5',
          borderColor: '#757575',
          icon: <Info />,
          severity: 0,
          pulse: false,
        };
    }
  };

  const config = getRiskConfig(riskLevel);
  const shouldAnimate = animated && config.pulse && config.severity >= 5;

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          fontSize: '0.75rem',
          padding: '2px 8px',
          iconSize: '0.9rem',
        };
      case 'large':
        return {
          fontSize: '1rem',
          padding: '6px 16px',
          iconSize: '1.3rem',
        };
      default:
        return {
          fontSize: '0.875rem',
          padding: '4px 12px',
          iconSize: '1.1rem',
        };
    }
  };

  const sizeStyles = getSizeStyles();

  // Tooltip content
  const tooltipContent = (
    <Box>
      {riskMessage && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {riskMessage}
        </Typography>
      )}
      {riskFactors.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Risk Factors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {riskFactors.map((factor, index) => (
              <li key={index}>
                <Typography variant="caption">{factor}</Typography>
              </li>
            ))}
          </ul>
        </>
      )}
    </Box>
  );

  // Chip variant
  if (variant === 'chip') {
    return (
      <Tooltip
        title={riskMessage || riskFactors.length > 0 ? tooltipContent : ''}
        arrow
        placement="top"
      >
        <Chip
          label={config.label}
          icon={showIcon ? React.cloneElement(config.icon, { 
            sx: { fontSize: sizeStyles.iconSize } 
          }) : undefined}
          onClick={onClick}
          sx={{
            backgroundColor: config.backgroundColor,
            color: config.color,
            border: `2px solid ${config.borderColor}`,
            fontSize: sizeStyles.fontSize,
            padding: sizeStyles.padding,
            fontWeight: 600,
            ...(shouldAnimate && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { 
                  opacity: 1,
                  transform: 'scale(1)',
                },
                '50%': { 
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                },
                '100%': { 
                  opacity: 1,
                  transform: 'scale(1)',
                },
              },
            }),
            ...(onClick && {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: config.color,
                color: 'white',
                '& .MuiChip-icon': {
                  color: 'white',
                },
              },
            }),
          }}
        />
      </Tooltip>
    );
  }

  // Badge variant (for use on cards)
  if (variant === 'badge') {
    return (
      <Badge
        badgeContent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: config.backgroundColor,
              color: config.color,
              border: `2px solid ${config.borderColor}`,
              borderRadius: 2,
              px: 1,
              py: 0.5,
              ...(shouldAnimate && {
                animation: 'pulse 2s infinite',
              }),
            }}
          >
            {showIcon && React.cloneElement(config.icon, { 
              sx: { fontSize: sizeStyles.iconSize } 
            })}
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {config.label}
            </Typography>
          </Box>
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box />
      </Badge>
    );
  }

  // Card variant (standalone card)
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        backgroundColor: config.backgroundColor,
        borderLeft: `4px solid ${config.borderColor}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s',
        ...(onClick && {
          '&:hover': {
            elevation: 4,
            transform: 'translateY(-2px)',
          },
        }),
        ...(shouldAnimate && {
          animation: 'pulse 2s infinite',
        }),
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {showIcon && React.cloneElement(config.icon, { 
          sx: { fontSize: '1.5rem', color: config.color } 
        })}
        <Typography 
          variant="subtitle1" 
          sx={{ fontWeight: 600, color: config.color }}
        >
          {config.label}
        </Typography>
      </Box>
      {riskMessage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {riskMessage}
        </Typography>
      )}
      {riskFactors.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Key Indicators:
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {riskFactors.slice(0, 3).map((factor, index) => (
              <Box component="li" key={index}>
                <Typography variant="caption" color="text.secondary">
                  {factor}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default FraudRiskBadge;